import { CRS, Point } from 'leaflet';
import { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, useMap, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-gesture-handling/dist/leaflet-gesture-handling.css';
import { Skeleton } from '@mui/material';
import { isEqual, mean } from 'lodash';
import MapContent from './components/MapContent';
import type { LeafletMapProps, PaCommonMapProps } from './types';

const PaCommonMap = (props: Readonly<PaCommonMapProps>) => {
  const { center, mapCoordinates, defaultZoom = 12, visualEsr, loading, height, width } = props;
  const resolvedHeight = height ?? '500px';
  const resolvedWidth = width ?? '100%';
  const currentCenter = useRef(center);

  const zoomState = useState(defaultZoom);
  const [zoom, setZoom] = zoomState;

  const visualEsrBounds = useMemo(() => {
    if (!visualEsr) return undefined;
    const minBound = CRS.EPSG3857.unproject(
      new Point(visualEsr.bounds.x[0], visualEsr.bounds.y[0]),
    );
    const maxBound = CRS.EPSG3857.unproject(
      new Point(visualEsr.bounds.x[1], visualEsr.bounds.y[1]),
    );

    return {
      ...visualEsr,
      bounds: { x: [minBound.lat, minBound.lng], y: [maxBound.lat, maxBound.lng] },
    } satisfies LeafletMapProps['visualEsr'];
  }, [visualEsr]);

  const mapCenter = useMemo((): [number, number] | undefined => {
    if (!visualEsrBounds) return center;
    return [
      mean([visualEsrBounds.bounds.x[0], visualEsrBounds.bounds.y[0]]),
      mean([visualEsrBounds.bounds.x[1], visualEsrBounds.bounds.y[1]]),
    ];
  }, [center, visualEsrBounds]);

  function MapEventsHandler() {
    const map = useMapEvents({
      click: (e) => {
        setZoom(map.getZoom());
        if (mapCoordinates) {
          mapCoordinates({
            latitude: e.latlng.lat,
            longitude: (((e.latlng.lng % 360) + 540) % 360) - 180,
          });
        }
      },
    });
    return null;
  }

  function ChangeMapView() {
    const map = useMap();
    if (!isEqual(currentCenter.current, center)) {
      if (center) {
        map.setView({ lat: center[0], lng: center[1] }, zoom);
      }
      currentCenter.current = center;
    }
    return null;
  }

  function FixSizeOnMount() {
    const map = useMap();
    useEffect(() => {
      const timeoutId = window.setTimeout(() => {
        if (!map.getContainer().isConnected) return;
        map.invalidateSize();
      }, 0);

      return () => {
        window.clearTimeout(timeoutId);
      };
    }, [map]);
    return null;
  }

  if (loading || !mapCenter) {
    return (
      <Skeleton variant="rectangular" height={resolvedHeight} width={resolvedWidth} aria-busy />
    );
  }

  return (
    <div data-visual-test="transparent" style={{ height: resolvedHeight, width: resolvedWidth }}>
      <MapContainer
        center={mapCenter}
        zoom={zoom}
        style={{
          height: '100%',
          width: '100%',
          overflowY: 'hidden',
          overflowX: 'hidden',
        }}
        zoomControl={false}
        attributionControl={false}
        className="arcgis-map"
        id="arcgis-map"
      >
        <MapEventsHandler />
        <ChangeMapView />
        <FixSizeOnMount />
        <MapContent {...props} zoomState={zoomState} />
      </MapContainer>
    </div>
  );
};

export default PaCommonMap;
